/**
 * Q&A の CSV 一括(2026-09-17。発注者指示「QAは随時アップデートするので、管理が追加/修正/削除とCSVで一括でできるように」)。
 * 列: ID(空欄=新規)／カテゴリ／質問／回答／表示順。書き出したCSVをそのまま直して取り込める。
 * CSV に無い既存の項目は、画面で「削除する」を選んだときだけ消す(既定は残す)。
 */
import { parseCsv } from '@core/lib/csv';
import type { QaInput, QaItem } from './qaRepo';

export const QA_CSV_HEADERS = ['ID', 'カテゴリ', '質問', '回答', '表示順'] as const;

/** 表示順: 表示順 → カテゴリ → 質問 */
export function sortQa<T extends Pick<QaItem, 'sort_order' | 'category' | 'question'>>(items: T[]): T[] {
  return [...items].sort((a, b) => a.sort_order - b.sort_order || a.category.localeCompare(b.category, 'ja') || a.question.localeCompare(b.question, 'ja'));
}

const quote = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

export function qaToCsv(items: QaItem[]): string {
  const lines = [QA_CSV_HEADERS.join(',')];
  for (const q of sortQa(items)) lines.push([q.id, q.category, q.question, q.answer, String(q.sort_order)].map(quote).join(','));
  return lines.join('\r\n') + '\r\n';
}

export type QaCsvError = { row: number; message: string };

export type QaImportPlan = {
  creates: QaInput[];
  updates: { item: QaItem; next: QaInput }[];
  unchanged: number;
  /** CSV に無い既存の項目(削除候補) */
  missing: QaItem[];
  errors: QaCsvError[];
};

/** 改行コードをそろえ、前後の空白を落とす(回答の中の改行は残す) */
const clean = (s: string | undefined) => (s ?? '').replace(/\r\n?/g, '\n').trim();

/**
 * CSV を読み、既存の Q&A と突き合わせて 追加・変更・変更なし・CSVに無い項目 に分ける。
 * エラーが1件でもあれば取り込まない(画面でエラーの行を示す)
 */
export function planQaImport(text: string, existing: QaItem[]): QaImportPlan {
  const table = parseCsv(text.replace(/^﻿/, ''));
  const headers = table.headers.map((h) => h.trim());
  const col = (name: string) => headers.indexOf(name);
  const plan: QaImportPlan = { creates: [], updates: [], unchanged: 0, missing: [], errors: [] };
  const missingCols = ['カテゴリ', '質問', '回答'].filter((h) => col(h) < 0);
  if (missingCols.length > 0) {
    plan.errors.push({ row: 1, message: `列がありません: ${missingCols.join('、')}(1行目は ${QA_CSV_HEADERS.join(',')})` });
    return plan;
  }
  const byId = new Map(existing.map((q) => [q.id, q]));
  const seenIds = new Set<string>();
  table.rows.forEach((cells, i) => {
    const row = i + 2;
    const id = col('ID') >= 0 ? clean(cells[col('ID')]) : '';
    const category = clean(cells[col('カテゴリ')]);
    const question = clean(cells[col('質問')]);
    const answer = clean(cells[col('回答')]);
    const orderText = col('表示順') >= 0 ? clean(cells[col('表示順')]) : '';
    const problems: string[] = [];
    if (!category) problems.push('カテゴリが空です');
    if (!question) problems.push('質問が空です');
    if (!answer) problems.push('回答が空です');
    let sortOrder = (i + 1) * 10;
    if (orderText !== '') {
      const n = Number(orderText.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0)));
      if (!Number.isInteger(n) || n < 0) problems.push('表示順は0以上の整数にしてください');
      else sortOrder = n;
    }
    if (id) {
      if (seenIds.has(id)) problems.push(`ID ${id} が2回あります`);
      else if (!byId.has(id)) problems.push(`ID ${id} の項目がありません(新しい項目は ID を空欄にしてください)`);
      seenIds.add(id);
    }
    if (problems.length > 0) {
      plan.errors.push({ row, message: problems.join('／') });
      return;
    }
    const next: QaInput = { category, question, answer, sort_order: sortOrder };
    const item = id ? byId.get(id) : undefined;
    if (!item) plan.creates.push(next);
    else if (item.category === next.category && item.question === next.question && item.answer === next.answer && item.sort_order === next.sort_order) plan.unchanged++;
    else plan.updates.push({ item, next });
  });
  plan.missing = existing.filter((q) => !seenIds.has(q.id));
  return plan;
}
